/**
 * DOM damage numbers (ui-ux §10.2, art §8.6): a pool of 16 `<span class="mc-dmg">` in a slot's `.mc-anchors`, placed by
 * the lane solver, positioned only by `transform`, animated with WAAPI. The pop runs in real time (it reads during
 * hit-stop); the rise runs in game time — {@link DamageNumbers.setTimeScale} drives its `playbackRate` (0 = frozen).
 * Reduced motion: no pop, fade only. Owner: UI/Visual (F3).
 */
import { formatNumberRu } from '../../shared';
import { LaneStacks, solveLane, type LaneInput } from './numberLane';

export type DmgKind = 'hit' | 'heavy' | 'crit' | 'holy' | 'fire' | 'poison' | 'dot' | 'curse' | 'heal' | 'word' | 'miss';

const POOL = 16;

interface Live { el: HTMLSpanElement; id: number; victim: string; rise: Animation | null; pop: Animation | null; fade: Animation | null; x: number; y: number }

export class DamageNumbers {
  private readonly pool: HTMLSpanElement[] = [];
  private readonly live = new Map<number, Live>();
  private readonly stacks = new LaneStacks();
  private seq = 0;
  private timeScale = 1;
  enabled = true;

  constructor(private readonly host: HTMLElement) {
    for (let i = 0; i < POOL; i++) {
      const el = document.createElement('span');
      el.className = 'mc-dmg';
      el.hidden = true;
      el.setAttribute('aria-hidden', 'true');
      host.appendChild(el);
      this.pool.push(el);
    }
  }

  /** Game-time scale for the rise (hit-stop 0, koSlowmo 0.35, ×2 speed …). */
  setTimeScale(s: number): void {
    this.timeScale = s;
    for (const l of this.live.values()) { if (l.rise) l.rise.playbackRate = s; if (l.fade) l.fade.playbackRate = s; }
  }

  /**
   * Spawns a number. `lane` describes the victim and the obstacles; `text` is the value («−12», «+8», «Мимо!»).
   * Words still show when «Числа урона» is off (they carry meaning).
   */
  spawn(kind: DmgKind, value: number | string, lane: Omit<LaneInput, 'boxW' | 'boxH'>, victim: string): number | null {
    const isWord = kind === 'word' || kind === 'miss';
    if (!this.enabled && !isWord) return null;
    const el = this.pool.find((p) => p.hidden) ?? this.oldest();
    if (!el) return null;
    const text = typeof value === 'number' ? (kind === 'heal' ? `+${formatNumberRu(value)}` : formatNumberRu(value)) : value;
    const size = kind === 'crit' ? 34 : kind === 'heavy' ? 26 : kind === 'dot' || kind === 'poison' ? 16 : isWord ? 18 : 20;
    const boxW = Math.round(size * 0.62 * text.length + (kind === 'crit' ? 20 : 6));
    const boxH = size + (kind === 'crit' ? 8 : 2);
    const r = solveLane({ ...lane, boxW, boxH });
    const id = ++this.seq;
    el.className = `mc-dmg mc-dmg--${kind === 'hit' ? 'light' : kind}`;
    el.textContent = text;
    el.hidden = false;
    const cx = r.x + boxW / 2, top = r.y0 + boxH;
    el.style.transform = `translate3d(${cx}px, ${top}px, 0)`;
    const reduced = /^(reduced|min)$/.test(document.documentElement.dataset.motion ?? '');
    const live: Live = { el, id, victim, rise: null, pop: null, fade: null, x: cx, y: top };
    this.live.set(id, live);
    const { fade, moved } = this.stacks.push(victim, id, r.y0, lane.headTopY + 8);
    for (const f of fade) this.fadeOut(f, 120);
    for (const m of moved) { const l = this.live.get(m.id); if (l) { l.y -= 22; l.el.style.transform = `translate3d(${l.x}px, ${l.y}px, 0)`; } }
    if (!reduced && kind !== 'dot' && kind !== 'poison') {
      live.pop = el.animate([{ scale: 1.4 }, { scale: 1 }], { duration: 90, easing: 'cubic-bezier(.34,1.56,.64,1)' });
    }
    live.rise = el.animate([{ translate: '-50% -100%' }, { translate: `-50% calc(-100% - ${reduced ? 0 : r.rise}px)` }], { duration: 700, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards' });
    live.rise.playbackRate = this.timeScale;
    live.fade = el.animate([{ opacity: 1 }, { opacity: 1, offset: 0.714 }, { opacity: 0 }], { duration: 700, fill: 'forwards' });
    live.fade.playbackRate = this.timeScale;
    live.fade.finished.then(() => this.release(id), () => undefined);
    return id;
  }

  private oldest(): HTMLSpanElement | null {
    const first = [...this.live.values()].sort((a, b) => a.id - b.id)[0];
    if (!first) return null;
    this.release(first.id);
    return first.el;
  }

  private fadeOut(id: number, ms: number): void {
    const l = this.live.get(id);
    if (!l) return;
    l.fade?.cancel();
    l.fade = l.el.animate([{ opacity: getComputedStyle(l.el).opacity }, { opacity: 0 }], { duration: ms, fill: 'forwards' });
    l.fade.finished.then(() => this.release(id), () => undefined);
  }

  private release(id: number): void {
    const l = this.live.get(id);
    if (!l) return;
    l.rise?.cancel(); l.pop?.cancel(); l.fade?.cancel();
    l.el.hidden = true;
    this.live.delete(id);
    this.stacks.remove(l.victim, id);
  }

  /** Alive count (tests, `__marg.ui`). */
  get alive(): number { return this.live.size; }

  clear(): void { for (const id of [...this.live.keys()]) this.release(id); }
  dispose(): void { this.clear(); for (const p of this.pool) p.remove(); }
}
